import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Trophy,
  TrendingDown,
  Search,
  Filter,
  Plus,
  Upload,
  Calendar,
  MapPin,
  Euro,
  Phone,
  User,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  MoreHorizontal,
  Download,
  Eye,
  Bot,
  Sparkles,
  X,
  Edit2,
  Trash2
} from 'lucide-react';

// Componente de tarjeta de contrato
const ContratoCard = ({ contrato }) => {
  const statusColors = {
    activo: 'bg-green-50 text-green-700 border-green-200',
    proximo_renovar: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    vencido: 'bg-red-50 text-red-700 border-red-200',
    pendiente: 'bg-blue-50 text-blue-700 border-blue-200'
  };

  const propiedadColors = {
    manantial: 'bg-blue-100 text-blue-800',
    franquiciado: 'bg-purple-100 text-purple-800',
    mixto: 'bg-orange-100 text-orange-800'
  };

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FileText className="w-4 h-4 text-[#6B7280]" />
          </div>
          <div>
            <p className="font-medium text-[#111827] text-sm">{contrato.nombre}</p>
            <p className="text-xs text-[#6B7280]">{contrato.numero}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-3 h-3 text-[#9CA3AF]" />
          <span className="text-sm text-[#6B7280]">{contrato.ciudad}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${propiedadColors[contrato.propiedad]}`}>
          {contrato.propiedad === 'manantial' ? '100% Manantial' :
           contrato.propiedad === 'franquiciado' ? `${contrato.porcentajeFranq}% Franq.` : 'Mixto'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm">
          <p className="text-[#111827] font-medium">{contrato.valorAnual}</p>
          <p className="text-xs text-[#6B7280]">/año</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <Calendar className="w-3 h-3" />
          <span>{contrato.fechaRenovacion}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${statusColors[contrato.estado]}`}>
          {contrato.estado === 'activo' ? 'Activo' :
           contrato.estado === 'proximo_renovar' ? 'Prox. Renovar' :
           contrato.estado === 'vencido' ? 'Vencido' : 'Pendiente'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Ver contrato">
            <Eye className="w-4 h-4 text-[#6B7280]" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Descargar PDF">
            <Download className="w-4 h-4 text-[#6B7280]" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Componente de tarjeta de concurso
const ConcursoCard = ({ concurso, onChangeStatus }) => {
  const statusColors = {
    presentado: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: AlertCircle },
    ganado: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle },
    perdido: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: XCircle }
  };

  const status = statusColors[concurso.estado];
  const StatusIcon = status.icon;

  return (
    <div className={`${status.bg} border ${status.border} rounded-xl p-4 transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${status.text}`} />
          <h4 className="font-medium text-[#111827]">{concurso.nombre}</h4>
        </div>
        <button className="p-1 hover:bg-white/50 rounded-lg">
          <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
        </button>
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <Building2 className="w-4 h-4" />
          <span>{concurso.lugar}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <MapPin className="w-4 h-4" />
          <span>{concurso.ciudad}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <Euro className="w-4 h-4" />
          <span className="font-medium text-[#111827]">{concurso.valorEstimado}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <Calendar className="w-4 h-4" />
          <span>Fecha limite: {concurso.fechaLimite}</span>
        </div>
      </div>

      {concurso.contacto && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-[#6B7280] mb-1">Contacto:</p>
          <div className="flex items-center gap-2">
            <User className="w-3 h-3 text-[#9CA3AF]" />
            <span className="text-sm">{concurso.contacto.nombre}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Phone className="w-3 h-3 text-[#9CA3AF]" />
            <span className="text-sm text-[#6B7280]">{concurso.contacto.telefono}</span>
          </div>
        </div>
      )}

      {concurso.estado === 'presentado' && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => onChangeStatus(concurso.id, 'ganado')}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Ganado
          </button>
          <button
            onClick={() => onChangeStatus(concurso.id, 'perdido')}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
          >
            <XCircle className="w-4 h-4" />
            Perdido
          </button>
        </div>
      )}

      {concurso.estado === 'ganado' && (
        <div className="mt-4 p-3 bg-green-100 rounded-lg">
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <Bot className="w-4 h-4" />
            <span className="font-medium">XIA notificado a:</span>
          </div>
          <p className="text-xs text-green-600 mt-1">SAT, Entradas, Administracion, Diseno</p>
        </div>
      )}

      {concurso.estado === 'perdido' && concurso.motivoPerdida && (
        <div className="mt-4 p-3 bg-red-100/50 rounded-lg">
          <p className="text-xs text-red-700">
            <span className="font-medium">Motivo:</span> {concurso.motivoPerdida}
          </p>
          {concurso.diferenciaCompetencia && (
            <p className="text-xs text-red-600 mt-1">
              Diferencia: {concurso.diferenciaCompetencia}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Componente de informe de competencia
const CompetenciaCard = ({ informe }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-[#111827]">{informe.concurso}</h4>
          <p className="text-sm text-[#6B7280] mt-1">{informe.competidor}</p>
        </div>
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
          informe.resultado === 'perdido' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>
          {informe.resultado === 'perdido' ? 'Perdido' : 'Ganado'}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-[#6B7280]">Nuestra oferta</p>
          <p className="text-lg font-semibold text-[#111827]">{informe.nuestraOferta}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-[#6B7280]">Competencia</p>
          <p className="text-lg font-semibold text-[#111827]">{informe.ofertaCompetencia}</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#6B7280]">Diferencia de precio:</span>
          <span className={`font-medium ${informe.diferencia > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {informe.diferencia > 0 ? '+' : ''}{informe.diferenciaPorcentaje}
          </span>
        </div>
      </div>

      {informe.analisis && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700 text-sm mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="font-medium">Analisis XIA</span>
          </div>
          <p className="text-sm text-blue-600">{informe.analisis}</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-[#9CA3AF]">
        <span>Fecha: {informe.fecha}</span>
        <button className="text-[#3B82F6] hover:underline">Ver detalle</button>
      </div>
    </div>
  );
};

// Zona de arrastrar y soltar para contratos
const DropZone = ({ onDrop }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onDrop(files);
    }
  }, [onDrop]);

  return (
    <div
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
        isDragging
          ? 'border-[#3B82F6] bg-[#3B82F6]/5'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex flex-col items-center">
        <div className={`p-3 rounded-full mb-3 ${isDragging ? 'bg-[#3B82F6]/10' : 'bg-gray-100'}`}>
          <Upload className={`w-6 h-6 ${isDragging ? 'text-[#3B82F6]' : 'text-[#6B7280]'}`} />
        </div>
        <p className="text-sm font-medium text-[#111827]">
          {isDragging ? 'Suelta el archivo aqui' : 'Arrastra un contrato PDF'}
        </p>
        <p className="text-xs text-[#6B7280] mt-1">
          XIA extraera los datos automaticamente
        </p>
        <div className="flex items-center gap-2 mt-3">
          <Bot className="w-4 h-4 text-[#3B82F6]" />
          <span className="text-xs text-[#3B82F6]">Procesado por XIA</span>
        </div>
      </div>
    </div>
  );
};

export default function ComercialPanel() {
  const [activeTab, setActiveTab] = useState('contratos');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [concursoFilter, setConcursoFilter] = useState('todos');

  // Estados para modales
  const [showNewContrato, setShowNewContrato] = useState(false);
  const [showNewConcurso, setShowNewConcurso] = useState(false);
  const [editingContrato, setEditingContrato] = useState(null);
  const [editingConcurso, setEditingConcurso] = useState(null);

  // Form para nuevo contrato
  const [contratoForm, setContratoForm] = useState({
    nombre: '',
    numero: '',
    ciudad: '',
    propiedad: 'manantial',
    porcentajeFranq: 0,
    valorAnual: '',
    fechaRenovacion: '',
    estado: 'pendiente',
    servicios: []
  });

  // Form para nuevo concurso
  const [concursoForm, setConcursoForm] = useState({
    nombre: '',
    lugar: '',
    ciudad: '',
    valorEstimado: '',
    fechaLimite: '',
    estado: 'presentado',
    contactoNombre: '',
    contactoTelefono: ''
  });

  const [contratos, setContratos] = useState([
    { id: 1, nombre: 'Teatro Principal', numero: 'CTR-2026-001', ciudad: 'Valencia', propiedad: 'manantial', valorAnual: '€24.000', fechaRenovacion: '15 Mar 2026', estado: 'activo', servicios: ['Taquillas', 'Control acceso'] },
    { id: 2, nombre: 'Auditorio Municipal', numero: 'CTR-2026-002', ciudad: 'Alicante', propiedad: 'franquiciado', porcentajeFranq: 30, valorAnual: '€18.500', fechaRenovacion: '28 Feb 2026', estado: 'proximo_renovar', servicios: ['Taquillas'] },
    { id: 3, nombre: 'Palacio de Congresos', numero: 'CTR-2025-089', ciudad: 'Murcia', propiedad: 'manantial', valorAnual: '€45.000', fechaRenovacion: '10 Ene 2026', estado: 'vencido', servicios: ['Taquillas', 'Control acceso', 'Parking'] },
    { id: 4, nombre: 'Estadio Municipal', numero: 'CTR-2026-003', ciudad: 'Castellon', propiedad: 'mixto', valorAnual: '€32.000', fechaRenovacion: '20 Abr 2026', estado: 'activo', servicios: ['Control acceso'] },
    { id: 5, nombre: 'Recinto Ferial', numero: 'CTR-2026-004', ciudad: 'Barcelona', propiedad: 'manantial', valorAnual: '€56.000', fechaRenovacion: '01 Jun 2026', estado: 'pendiente', servicios: ['Taquillas', 'Control acceso', 'Kioscos'] }
  ]);

  const [concursos, setConcursos] = useState([
    { id: 1, nombre: 'Licitacion Teatro Real', lugar: 'Teatro Real', ciudad: 'Madrid', valorEstimado: '€85.000', fechaLimite: '25 Ene 2026', estado: 'presentado', contacto: { nombre: 'Ana Garcia', telefono: '612 345 678' } },
    { id: 2, nombre: 'Concurso Palau de la Musica', lugar: 'Palau de la Musica', ciudad: 'Barcelona', valorEstimado: '€62.000', fechaLimite: '15 Feb 2026', estado: 'presentado', contacto: { nombre: 'Marc Puig', telefono: '623 456 789' } },
    { id: 3, nombre: 'Auditorio Nacional', lugar: 'Auditorio Nacional', ciudad: 'Madrid', valorEstimado: '€120.000', fechaLimite: '10 Ene 2026', estado: 'ganado', contacto: { nombre: 'Carlos Lopez', telefono: '634 567 890' } },
    { id: 4, nombre: 'Gran Teatro Liceu', lugar: 'Gran Teatro Liceu', ciudad: 'Barcelona', valorEstimado: '€95.000', fechaLimite: '05 Ene 2026', estado: 'perdido', motivoPerdida: 'Precio competidor mas bajo', diferenciaCompetencia: '-15% vs competencia', contacto: { nombre: 'Laura Vidal', telefono: '645 678 901' } },
    { id: 5, nombre: 'Teatro Arriaga', lugar: 'Teatro Arriaga', ciudad: 'Bilbao', valorEstimado: '€45.000', fechaLimite: '20 Feb 2026', estado: 'presentado', contacto: { nombre: 'Iker Etxebarria', telefono: '656 789 012' } }
  ]);

  const [informesCompetencia] = useState([
    { id: 1, concurso: 'Gran Teatro Liceu', competidor: 'TicketMaster Spain', resultado: 'perdido', nuestraOferta: '€95.000', ofertaCompetencia: '€81.000', diferencia: 14000, diferenciaPorcentaje: '+17%', fecha: '05 Ene 2026', analisis: 'Competidor ofrecio menor precio sacrificando margen. Recomendacion: revisar estructura de costes para este tipo de recintos.' },
    { id: 2, concurso: 'Auditorio Nacional', competidor: 'EntradaYa', resultado: 'ganado', nuestraOferta: '€120.000', ofertaCompetencia: '€135.000', diferencia: -15000, diferenciaPorcentaje: '-11%', fecha: '10 Ene 2026', analisis: 'Ganado por mejor relacion calidad-precio y referencias previas.' },
    { id: 3, concurso: 'Palacio Deportes', competidor: 'EventPro', resultado: 'perdido', nuestraOferta: '€78.000', ofertaCompetencia: '€72.000', diferencia: 6000, diferenciaPorcentaje: '+8%', fecha: '20 Dic 2025', analisis: 'Diferencia de precio minima pero competidor tenia relacion previa con el cliente.' }
  ]);

  const tabs = [
    { id: 'contratos', label: 'Contratos', icon: FileText },
    { id: 'concursos', label: 'Concursos', icon: Trophy },
    { id: 'competencia', label: 'Informes Competencia', icon: TrendingDown }
  ];

  const concursoFilters = [
    { id: 'todos', label: 'Todos' },
    { id: 'presentado', label: 'Presentados' },
    { id: 'ganado', label: 'Ganados' },
    { id: 'perdido', label: 'Perdidos' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleFileDrop = (files) => {
    console.log('Archivos recibidos:', files);
    // Aqui se enviaria a XIA para procesar
    alert(`XIA procesara ${files.length} archivo(s). Los datos se extraeran automaticamente.`);
  };

  const handleConcursoStatusChange = (id, newStatus) => {
    setConcursos(prev => prev.map(c =>
      c.id === id ? { ...c, estado: newStatus } : c
    ));

    if (newStatus === 'ganado') {
      alert('XIA ha enviado notificaciones automaticas a:\n- SAT: Preparar taquillas, lineas, pinpads\n- Entradas: Crear proveedor, altas\n- Administracion: Facturar, alta cliente\n- Diseno: Diseno entradas, carteleria');
    }
  };

  // Crear nuevo contrato
  const handleCreateContrato = () => {
    if (!contratoForm.nombre.trim()) {
      alert('El nombre del contrato es obligatorio');
      return;
    }
    const newContrato = {
      id: Date.now(),
      nombre: contratoForm.nombre,
      numero: contratoForm.numero || `CTR-2026-${String(contratos.length + 1).padStart(3, '0')}`,
      ciudad: contratoForm.ciudad,
      propiedad: contratoForm.propiedad,
      porcentajeFranq: contratoForm.porcentajeFranq,
      valorAnual: contratoForm.valorAnual ? `€${contratoForm.valorAnual}` : '€0',
      fechaRenovacion: contratoForm.fechaRenovacion || 'Por definir',
      estado: contratoForm.estado,
      servicios: contratoForm.servicios
    };
    setContratos(prev => [...prev, newContrato]);
    closeContratoModal();
  };

  // Editar contrato
  const handleEditContrato = (contrato) => {
    setContratoForm({
      nombre: contrato.nombre,
      numero: contrato.numero,
      ciudad: contrato.ciudad,
      propiedad: contrato.propiedad,
      porcentajeFranq: contrato.porcentajeFranq || 0,
      valorAnual: contrato.valorAnual?.replace('€', '').replace('.', '') || '',
      fechaRenovacion: contrato.fechaRenovacion,
      estado: contrato.estado,
      servicios: contrato.servicios || []
    });
    setEditingContrato(contrato);
    setShowNewContrato(true);
  };

  // Guardar edición contrato
  const handleUpdateContrato = () => {
    if (!contratoForm.nombre.trim()) {
      alert('El nombre del contrato es obligatorio');
      return;
    }
    setContratos(prev => prev.map(c =>
      c.id === editingContrato.id
        ? {
            ...c,
            nombre: contratoForm.nombre,
            numero: contratoForm.numero,
            ciudad: contratoForm.ciudad,
            propiedad: contratoForm.propiedad,
            porcentajeFranq: contratoForm.porcentajeFranq,
            valorAnual: contratoForm.valorAnual ? `€${contratoForm.valorAnual}` : c.valorAnual,
            fechaRenovacion: contratoForm.fechaRenovacion,
            estado: contratoForm.estado,
            servicios: contratoForm.servicios
          }
        : c
    ));
    closeContratoModal();
  };

  // Eliminar contrato
  const handleDeleteContrato = (id) => {
    if (!window.confirm('¿Eliminar este contrato?')) return;
    setContratos(prev => prev.filter(c => c.id !== id));
  };

  // Cerrar modal contrato
  const closeContratoModal = () => {
    setShowNewContrato(false);
    setEditingContrato(null);
    setContratoForm({
      nombre: '',
      numero: '',
      ciudad: '',
      propiedad: 'manantial',
      porcentajeFranq: 0,
      valorAnual: '',
      fechaRenovacion: '',
      estado: 'pendiente',
      servicios: []
    });
  };

  // Crear nuevo concurso
  const handleCreateConcurso = () => {
    if (!concursoForm.nombre.trim()) {
      alert('El nombre del concurso es obligatorio');
      return;
    }
    const newConcurso = {
      id: Date.now(),
      nombre: concursoForm.nombre,
      lugar: concursoForm.lugar || concursoForm.nombre,
      ciudad: concursoForm.ciudad,
      valorEstimado: concursoForm.valorEstimado ? `€${concursoForm.valorEstimado}` : 'Por definir',
      fechaLimite: concursoForm.fechaLimite || 'Por definir',
      estado: concursoForm.estado,
      contacto: concursoForm.contactoNombre ? {
        nombre: concursoForm.contactoNombre,
        telefono: concursoForm.contactoTelefono
      } : null
    };
    setConcursos(prev => [...prev, newConcurso]);
    closeConcursoModal();
  };

  // Editar concurso
  const handleEditConcurso = (concurso) => {
    setConcursoForm({
      nombre: concurso.nombre,
      lugar: concurso.lugar,
      ciudad: concurso.ciudad,
      valorEstimado: concurso.valorEstimado?.replace('€', '').replace('.', '') || '',
      fechaLimite: concurso.fechaLimite,
      estado: concurso.estado,
      contactoNombre: concurso.contacto?.nombre || '',
      contactoTelefono: concurso.contacto?.telefono || ''
    });
    setEditingConcurso(concurso);
    setShowNewConcurso(true);
  };

  // Guardar edición concurso
  const handleUpdateConcurso = () => {
    if (!concursoForm.nombre.trim()) {
      alert('El nombre del concurso es obligatorio');
      return;
    }
    setConcursos(prev => prev.map(c =>
      c.id === editingConcurso.id
        ? {
            ...c,
            nombre: concursoForm.nombre,
            lugar: concursoForm.lugar,
            ciudad: concursoForm.ciudad,
            valorEstimado: concursoForm.valorEstimado ? `€${concursoForm.valorEstimado}` : c.valorEstimado,
            fechaLimite: concursoForm.fechaLimite,
            estado: concursoForm.estado,
            contacto: concursoForm.contactoNombre ? {
              nombre: concursoForm.contactoNombre,
              telefono: concursoForm.contactoTelefono
            } : null
          }
        : c
    ));
    closeConcursoModal();
  };

  // Eliminar concurso
  const handleDeleteConcurso = (id) => {
    if (!window.confirm('¿Eliminar este concurso?')) return;
    setConcursos(prev => prev.filter(c => c.id !== id));
  };

  // Cerrar modal concurso
  const closeConcursoModal = () => {
    setShowNewConcurso(false);
    setEditingConcurso(null);
    setConcursoForm({
      nombre: '',
      lugar: '',
      ciudad: '',
      valorEstimado: '',
      fechaLimite: '',
      estado: 'presentado',
      contactoNombre: '',
      contactoTelefono: ''
    });
  };

  const filteredConcursos = concursoFilter === 'todos'
    ? concursos
    : concursos.filter(c => c.estado === concursoFilter);

  const kpis = {
    contratosActivos: contratos.filter(c => c.estado === 'activo').length,
    proximasRenovaciones: contratos.filter(c => c.estado === 'proximo_renovar').length,
    concursosPresentados: concursos.filter(c => c.estado === 'presentado').length,
    tasaConversion: Math.round((concursos.filter(c => c.estado === 'ganado').length / concursos.filter(c => c.estado !== 'presentado').length) * 100) || 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Comercial</h1>
          <p className="text-[#6B7280] mt-1">Contratos, concursos y analisis de competencia</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'contratos' && (
            <button
              onClick={() => setShowNewContrato(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo contrato</span>
            </button>
          )}
          {activeTab === 'concursos' && (
            <button
              onClick={() => setShowNewConcurso(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo concurso</span>
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Contratos activos</p>
              <p className="text-xl font-semibold text-[#111827]">{kpis.contratosActivos}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Proximas renovaciones</p>
              <p className="text-xl font-semibold text-[#111827]">{kpis.proximasRenovaciones}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Trophy className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Concursos presentados</p>
              <p className="text-xl font-semibold text-[#111827]">{kpis.concursosPresentados}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <TrendingDown className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Tasa conversion</p>
              <p className="text-xl font-semibold text-[#111827]">{kpis.tasaConversion}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-[#111827] shadow-sm'
                : 'text-[#6B7280] hover:text-[#111827]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'contratos' && (
        <div className="space-y-4">
          {/* Zona de arrastrar */}
          <DropZone onDrop={handleFileDrop} />

          {/* Buscador */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar contratos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>

          {/* Tabla de contratos */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Contrato</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Ciudad</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Propiedad</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Valor</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Renovacion</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-[#6B7280] uppercase">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {contratos.map((contrato) => (
                  <ContratoCard key={contrato.id} contrato={contrato} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'concursos' && (
        <div className="space-y-4">
          {/* Filtros de concursos */}
          <div className="flex items-center gap-2">
            {concursoFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setConcursoFilter(filter.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  concursoFilter === filter.id
                    ? 'bg-[#3B82F6] text-white'
                    : 'bg-white border border-gray-200 text-[#6B7280] hover:bg-gray-50'
                }`}
              >
                {filter.label}
                {filter.id !== 'todos' && (
                  <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                    {concursos.filter(c => c.estado === filter.id).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Grid de concursos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConcursos.map((concurso) => (
              <ConcursoCard
                key={concurso.id}
                concurso={concurso}
                onChangeStatus={handleConcursoStatusChange}
              />
            ))}
          </div>

          {filteredConcursos.length === 0 && (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-[#6B7280]">No hay concursos en esta categoria</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'competencia' && (
        <div className="space-y-4">
          {/* Buscador */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar informes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
              <Filter className="w-4 h-4" />
              Filtrar por competidor
            </button>
          </div>

          {/* Resumen */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <Bot className="w-6 h-6 text-[#3B82F6]" />
              <h3 className="font-semibold text-[#111827]">Resumen XIA - Competencia</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-sm text-[#6B7280]">Concursos analizados</p>
                <p className="text-xl font-semibold text-[#111827]">{informesCompetencia.length}</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-sm text-[#6B7280]">Diferencia media precio</p>
                <p className="text-xl font-semibold text-red-600">+8.5%</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-sm text-[#6B7280]">Principal competidor</p>
                <p className="text-xl font-semibold text-[#111827]">TicketMaster</p>
              </div>
            </div>
          </div>

          {/* Grid de informes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {informesCompetencia.map((informe) => (
              <CompetenciaCard key={informe.id} informe={informe} />
            ))}
          </div>
        </div>
      )}

      {/* Modal Nuevo/Editar Contrato */}
      {showNewContrato && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeContratoModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-[550px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-[#111827]">
                {editingContrato ? 'Editar Contrato' : 'Nuevo Contrato'}
              </h3>
              <button onClick={closeContratoModal} className="p-1 hover:bg-gray-200 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Nombre del contrato *</label>
                <input
                  type="text"
                  value={contratoForm.nombre}
                  onChange={(e) => setContratoForm({...contratoForm, nombre: e.target.value})}
                  placeholder="Ej: Teatro Principal"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Numero contrato</label>
                  <input
                    type="text"
                    value={contratoForm.numero}
                    onChange={(e) => setContratoForm({...contratoForm, numero: e.target.value})}
                    placeholder="CTR-2026-XXX"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={contratoForm.ciudad}
                    onChange={(e) => setContratoForm({...contratoForm, ciudad: e.target.value})}
                    placeholder="Madrid"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Propiedad</label>
                  <select
                    value={contratoForm.propiedad}
                    onChange={(e) => setContratoForm({...contratoForm, propiedad: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="manantial">100% Manantial</option>
                    <option value="franquiciado">Franquiciado</option>
                    <option value="mixto">Mixto</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Valor anual (€)</label>
                  <input
                    type="text"
                    value={contratoForm.valorAnual}
                    onChange={(e) => setContratoForm({...contratoForm, valorAnual: e.target.value})}
                    placeholder="24.000"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Fecha renovacion</label>
                  <input
                    type="text"
                    value={contratoForm.fechaRenovacion}
                    onChange={(e) => setContratoForm({...contratoForm, fechaRenovacion: e.target.value})}
                    placeholder="15 Mar 2026"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Estado</label>
                  <select
                    value={contratoForm.estado}
                    onChange={(e) => setContratoForm({...contratoForm, estado: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="activo">Activo</option>
                    <option value="proximo_renovar">Proximo a renovar</option>
                    <option value="vencido">Vencido</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t bg-gray-50">
              <button
                onClick={closeContratoModal}
                className="px-4 py-2.5 text-[#6B7280] hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingContrato ? handleUpdateContrato : handleCreateContrato}
                className="px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors"
              >
                {editingContrato ? 'Guardar cambios' : 'Crear contrato'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nuevo/Editar Concurso */}
      {showNewConcurso && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeConcursoModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-[550px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-[#111827]">
                {editingConcurso ? 'Editar Concurso' : 'Nuevo Concurso'}
              </h3>
              <button onClick={closeConcursoModal} className="p-1 hover:bg-gray-200 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Nombre del concurso *</label>
                <input
                  type="text"
                  value={concursoForm.nombre}
                  onChange={(e) => setConcursoForm({...concursoForm, nombre: e.target.value})}
                  placeholder="Ej: Licitacion Teatro Real"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Lugar</label>
                  <input
                    type="text"
                    value={concursoForm.lugar}
                    onChange={(e) => setConcursoForm({...concursoForm, lugar: e.target.value})}
                    placeholder="Teatro Real"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={concursoForm.ciudad}
                    onChange={(e) => setConcursoForm({...concursoForm, ciudad: e.target.value})}
                    placeholder="Madrid"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Valor estimado (€)</label>
                  <input
                    type="text"
                    value={concursoForm.valorEstimado}
                    onChange={(e) => setConcursoForm({...concursoForm, valorEstimado: e.target.value})}
                    placeholder="85.000"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Fecha limite</label>
                  <input
                    type="text"
                    value={concursoForm.fechaLimite}
                    onChange={(e) => setConcursoForm({...concursoForm, fechaLimite: e.target.value})}
                    placeholder="25 Ene 2026"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Estado</label>
                <select
                  value={concursoForm.estado}
                  onChange={(e) => setConcursoForm({...concursoForm, estado: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                >
                  <option value="presentado">Presentado</option>
                  <option value="ganado">Ganado</option>
                  <option value="perdido">Perdido</option>
                </select>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-[#374151] mb-3">Contacto</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">Nombre</label>
                    <input
                      type="text"
                      value={concursoForm.contactoNombre}
                      onChange={(e) => setConcursoForm({...concursoForm, contactoNombre: e.target.value})}
                      placeholder="Ana Garcia"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B7280] mb-1">Telefono</label>
                    <input
                      type="text"
                      value={concursoForm.contactoTelefono}
                      onChange={(e) => setConcursoForm({...concursoForm, contactoTelefono: e.target.value})}
                      placeholder="612 345 678"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t bg-gray-50">
              <button
                onClick={closeConcursoModal}
                className="px-4 py-2.5 text-[#6B7280] hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingConcurso ? handleUpdateConcurso : handleCreateConcurso}
                className="px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors"
              >
                {editingConcurso ? 'Guardar cambios' : 'Crear concurso'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
